import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Rnd } from 'react-rnd';
import { useIntl } from 'react-intl';

// // MUI components
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
const useStyles = makeStyles((theme) => ({
	title: {
		margin: 0,
		padding: theme.spacing(2),
		cursor: 'move',
	},
	closeButton: {
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(1),
		color: theme.palette.grey[500],
	},
	fullScreenButton: {
		position: 'absolute',
		right: theme.spacing(5),
		top: theme.spacing(1),
	},
}));

const dragHandleClassName = 'RnDDragHandler';

function PaperComponent({ size, resizable, ...rest }) {
	const theme = useTheme();
	const paperRef = React.useRef(null);
	const [position, setPosition] = React.useState({ x: 0, y: 0 });
	const [height, setHeight] = React.useState('auto');

	const updatePosition = React.useCallback(() => {
		if (!paperRef.current) return;
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		const rawWidth = theme.breakpoints.values[size];
		const maxAllowedWidth = viewportWidth * 0.9;
		const actualWidth = Math.min(rawWidth, maxAllowedWidth);

		const rect = paperRef.current.getBoundingClientRect();
		const clampedHeight = Math.min(rect.height, viewportHeight * 0.85);
		const y = Math.max((viewportHeight - clampedHeight) / 2, 20);
		const x = Math.max((viewportWidth - actualWidth) / 2, 0);

		setPosition({ x, y });
		setHeight(clampedHeight);
	}, [size, theme.breakpoints.values]);

	// Recalculate when mounted or resized
	React.useLayoutEffect(() => {
		updatePosition();
		const resizeObserver = new ResizeObserver(() => updatePosition());
		if (paperRef.current) resizeObserver.observe(paperRef.current);
		window.addEventListener('resize', updatePosition);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updatePosition);
		};
	}, [updatePosition]);

	const viewportWidth = window.innerWidth;
	const rawWidth = theme.breakpoints.values[size];
	const maxAllowedWidth = viewportWidth * 0.9;
	const actualWidth = Math.min(rawWidth, maxAllowedWidth);

	return (
		<Rnd
			size={{ width: actualWidth, height }}
			position={position}
			minWidth={300}
			minHeight={200}
			bounds="window"
			dragHandleClassName={dragHandleClassName}
			enableResizing={{
				top: false,
				topRight: false,
				topLeft: false,
				right: resizable,
				bottom: resizable,
				left: resizable,
				bottomRight: resizable,
				bottomLeft: resizable,
			}}
		>
			<Paper
				ref={paperRef}
				style={{
					width: '100%',
					height: 'auto',
					maxHeight: '85vh',
					overflow: 'hidden', // prevent outer overflow
					display: 'flex',
					flexDirection: 'column',
					position: 'absolute',
				}}
				{...rest}
			/>
		</Rnd>
	);
}

function RnDDialog({
	children,
	open,
	size,
	title,
	resizable,
	onClose,
	onActions,
	showFullScreen,
	onCloseText,
}) {
	const classes = useStyles();
	const intl = useIntl();
	const [isFullScreen, setIsFullScreen] = React.useState(false);

	React.useEffect(() => {
		if (open) setIsFullScreen(false);
	}, [open]);

	return (
		<Dialog
			open={open}
			fullWidth
			maxWidth={size}
			scroll="paper"
			fullScreen={isFullScreen}
			{...(!isFullScreen && {
				PaperComponent: PaperComponent,
				PaperProps: { size, resizable },
			})}
		>
			<DialogTitle className={cx(dragHandleClassName, classes.title)}>
				{title}
				{showFullScreen && (
					<IconButton
						aria-label="Toggle Fullscreen"
						onClick={() => setIsFullScreen((prev) => !prev)}
						className={classes.fullScreenButton}
					>
						{isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
					</IconButton>
				)}
				{onClose && (
					<IconButton aria-label="Close" className={classes.closeButton} onClick={onClose}>
						<CloseIcon />
					</IconButton>
				)}
			</DialogTitle>

			{/* Make inner content scrollable, not the outer RnD */}
			<DialogContent
				dividers
				// sx={{
				// 	flex: 1,
				// 	overflowY: 'auto',
				// 	maxHeight: 'calc(85vh - 130px)',
				// }}
				sx={{
					flex: 1,
					overflowY: 'auto',
					...(isFullScreen
						? {
								maxHeight: 'none',
								height: '100%',
						  }
						: {
								maxHeight: 'calc(85vh - 130px)',
						  }),
				}}
			>
				{children}
			</DialogContent>

			{(onClose || (Array.isArray(onActions) && onActions.length > 0)) && (
				<DialogActions>
					{onClose && (
						<Button variant="contained" onClick={onClose} color="primary">
							{onCloseText || intl.formatMessage({ id: 'GENERAL.Cancel' })}
						</Button>
					)}
					{onActions}
				</DialogActions>
			)}
		</Dialog>
	);
}

RnDDialog.propTypes = {
	open: PropTypes.bool,
	size: PropTypes.oneOf(['lg', 'md', 'sm', 'xl', 'xs']),
	title: PropTypes.string.isRequired,
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
	onClose: PropTypes.func,
	resizable: PropTypes.bool,
	onActions: PropTypes.node,
	showFullScreen: PropTypes.bool,
	onCloseText: PropTypes.string,
};

RnDDialog.defaultProps = {
	open: false,
	size: 'md',
	resizable: true,
	showFullScreen: false,
	onCloseText: 'Cancel', // Close/Cancel/Ok - But default is Cancel
};

export default RnDDialog;
